import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Lock, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between text-sm py-1.5 border-b border-stone-100 last:border-0" data-testid={testId}>
      <span className="text-stone-500">{label}</span>
      <span className="font-bold text-stone-800">{value}</span>
    </div>
  );
}

function CloseShiftDialog({ token, status, onClose, onClosed }) {
  const [report, setReport] = useState(null);
  const [clockOutAll, setClockOutAll] = useState(status.open_shifts.length > 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi(token).get("/admin/pos/report").then((r) => setReport(r.data)).catch(() => setReport({}));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi(token).post("/admin/shift/close", { clock_out_all: clockOutAll, resend: status.closed });
      toast.success(`Shift closed — report emailed${data.clocked_out?.length ? ` · clocked out ${data.clocked_out.join(", ")}` : ""}`);
      onClosed(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't close shift");
    } finally {
      setBusy(false);
    }
  };

  const sq = report?.square || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="close-shift-dialog">
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{status.date} · {status.closed ? "Re-send report" : "Close shift"}</div>
            <div className="font-black text-stone-800 text-lg">End-of-shift report</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>

        {!report ? (
          <div className="text-sm text-stone-400">Pulling today&apos;s numbers…</div>
        ) : (
          <div className="bg-stone-50 rounded-2xl px-4 py-2" data-testid="close-shift-snapshot">
            <Row label="App orders" value={`${report.order_count ?? 0} · ${fmt(report.gross)}`} testId="close-shift-app" />
            <Row label="Square register" value={sq.ok ? `${sq.payment_count} · ${fmt(sq.gross)}` : sq.configured ? "unavailable" : "not connected"} testId="close-shift-square" />
            <Row label="Labor" value={`${(report.labor_hours || 0).toFixed(2)}h · ${fmt(report.labor_cost)}`} testId="close-shift-labor" />
            <Row label="Still clocked in" value={status.open_shifts.length ? status.open_shifts.map((p) => p.name).join(", ") : "nobody"} testId="close-shift-open" />
          </div>
        )}

        {status.open_shifts.length > 0 && (
          <label className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm" data-testid="close-shift-clockout-toggle">
            <input type="checkbox" checked={clockOutAll} onChange={(e) => setClockOutAll(e.target.checked)} className="mt-1" data-testid="close-shift-clockout-checkbox" />
            <span>
              <b>Clock everyone out now</b> ({status.open_shifts.map((p) => p.name).join(", ")}).
              <span className="block text-[11px] text-stone-500">Their punches close at this moment and count in the report. Untick to leave them on the clock.</span>
            </span>
          </label>
        )}

        <div className="text-[11px] text-stone-500 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> The full report (app orders, Square register, labor) is emailed to you right away. If you forget, it goes out automatically at 11:00 PM Central.
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={confirm} disabled={busy || !report} data-testid="close-shift-confirm"
            className="flex-1 bg-stone-900 text-white py-3 rounded-full text-xs uppercase tracking-widest font-black disabled:opacity-50 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" /> {busy ? "Sending…" : status.closed ? "Re-send report" : "Close shift & email report"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-3 rounded-full border border-stone-300 text-xs uppercase tracking-widest font-bold text-stone-600">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function CloseShiftButton({ token, compact = false, onClosed }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/shift/status");
      setStatus(data);
    } catch {}
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!status) return null;
  const closedAt = status.closure?.closed_at;
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {status.closed && (
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="shift-closed-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Shift closed {fmtTime(closedAt)}{status.closure?.closed_by === "auto" ? " (auto)" : ""}
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="close-shift-btn"
          className={`flex items-center gap-1.5 rounded-full font-black uppercase tracking-widest ${
            compact ? "px-3 py-1.5 text-[11px]" : "px-4 py-2 text-xs"
          } ${status.closed ? "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100" : "bg-stone-900 text-white hover:bg-stone-700"}`}
        >
          <Lock className="w-3.5 h-3.5" /> {status.closed ? "Re-send report" : "Close shift"}
        </button>
      </div>
      {open && (
        <CloseShiftDialog token={token} status={status} onClose={() => setOpen(false)}
          onClosed={(c) => { setOpen(false); setStatus({ ...status, closed: true, closure: c, open_shifts: c.clocked_out?.length ? [] : status.open_shifts }); onClosed?.(); }} />
      )}
    </>
  );
}
